import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, inject, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { CldrIntlService, IntlService, load } from '@progress/kendo-angular-intl';
import likelySubtags from 'cldr-data/supplemental/likelySubtags.json';
import weekData from 'cldr-data/supplemental/weekData.json';
import currencyData from 'cldr-data/supplemental/currencyData.json';
import numbers from 'cldr-data/main/de/numbers.json';
import timeZoneNames from 'cldr-data/main/de/timeZoneNames.json';
import calendar from 'cldr-data/main/de/ca-gregorian.json';
import currencies from 'cldr-data/main/de/currencies.json';
import dateFields from 'cldr-data/main/de/dateFields.json';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { ApiModule } from './api/api.module';
import { AusschreibungService, FoerderprogrammService, PortalkonfigurationService } from './api/service-index';
import { StammdatenService } from './core/services/stammdaten.service';
import { NavigationEnd, NavigationStart, Router, RouterModule, ROUTES } from '@angular/router';
import { PortalContextService } from './core/services/portal-context.service';
import { SectionService } from './core/services/section.service';
import { environment } from '../environments/environment';
import { AuthenticationModule } from './core/components/authentication/authentication.module';
import { CoreSharedModule } from './core/core-shared.module';
import { AnfrageScopeHttpInterceptor } from './core/components/authentication/http/anfrage-scope.httpinterceptor';
import { TokenAuthenticationHttpInterceptor } from './core/components/authentication/http/token-authentication.httpinterceptor';
import { IdTokenRepository } from './core/components/authentication/repositories/abstractions/id-token.repository';
import { LocalStorageIdTokenRepository } from './core/components/authentication/repositories/localstorage-id-token.repository';
import { XFoundHttpClient } from './core/components/authentication/http/abstractions/xfound.httpclient';
import { GlobalNotificationHandler } from './core/utils/notifications/global-notification-handler';
import { TranslateModule } from '@ngx-translate/core';
import { AcceptedLanguageInterceptor } from './core/components/authentication/http/AcceptedLanguageInterceptor';
import { AnfrageFactory } from './core/factories/anfrage.factory';
import { AnfrageListFactory } from './core/factories/anfrage-list.factory';
import { XsrfTokenInterceptor } from './core/components/authentication/http/xrsf-token.httpinterceptor';
import { TempAnfrageIdHttpInterceptor } from './core/components/authentication/http/temp-anfrage-id.httpinterceptor';
import { AnfrageRoutingFactory } from './core/factories/anfrage-routing.factory';
import { ReportRoutingFactory } from './core/factories/report-routing.factory';
import { FundsRoutingFactory } from './core/factories/funds-routing.factory';
import { ReviewRoutingFactory } from './core/factories/review-routing.factory';
import { AuthenticationRoutingFactory } from './core/factories/authentication-routing.factory';
import { AdminRoutingFactory } from './core/factories/admin-routing.factory';
import { AusschreibungRoutingFactory } from './core/factories/ausschreibung-routing.factory';
import { AppRoutingFactory } from './core/factories/app-routing.factory';
import { MessageService } from '@progress/kendo-angular-l10n';
import { KendoMessageService } from './core/services/kendo-message-service';
import { ComponentService } from './core/services/component.service';
import { ExtendedAuthenticationService } from './core/components/authentication/services/extended-authentication.service';
import { AenderungsantragRoutingFactory } from './core/factories/aenderungsantrag-routing.factory';
import { ChatRoutingFactory } from './core/factories/chat-routing.factory';
import { ExtendedNotificationService } from './core/services/extended-notification.service';
import { NotificationService } from '@progress/kendo-angular-notification';
import { AnfrageLanguageService } from './core/services/anfrage-language.service';

registerLocaleData(localeDe, 'de-DE', localeDeExtra);

load(
  likelySubtags,
  weekData,
  currencyData,
  numbers,
  currencies,
  calendar,
  dateFields,
  timeZoneNames
);

export const httpInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useExisting: AnfrageScopeHttpInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useExisting: TempAnfrageIdHttpInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: TokenAuthenticationHttpInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: AcceptedLanguageInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useExisting: XsrfTokenInterceptor, multi: true }
];

@NgModule({
  declarations: [
    AppComponent   
  ],
  imports: [
    ApiModule.forRoot({ rootUrl: environment.xFoundRestUrl }),
    TranslateModule.forRoot({}),
    AuthenticationModule.forRoot({
      scope: environment.authentication.scope,
      loginUrl: environment.authentication.loginUrl,
      defaultRedirectUrl: environment.authentication.defaultRedirectUrl
    }),
    BrowserModule,
    BrowserAnimationsModule,
    LoggerModule.forRoot({
      level: NgxLoggerLevel.DEBUG,
    }),
    CoreSharedModule,
    RouterModule.forRoot([], {
      paramsInheritanceStrategy: 'always',
      onSameUrlNavigation: 'reload'
    }),
  ],
  providers: [
    {
      provide: ROUTES,
      multi: true,
      useFactory: () => {
        const routingFactory = inject(AppRoutingFactory);
        return routingFactory.create();
      },
    
    },
    HttpClient,
    PortalkonfigurationService,
    AusschreibungService,
    FoerderprogrammService,
    AnfrageScopeHttpInterceptor,
    PortalContextService,
    AnfrageFactory,
    AnfrageListFactory,
    GlobalNotificationHandler,
    AppRoutingFactory,
    AnfrageRoutingFactory,
    ReportRoutingFactory,
    FundsRoutingFactory,
    ReviewRoutingFactory,
    AuthenticationRoutingFactory,
    AdminRoutingFactory,
    AusschreibungRoutingFactory,
    AenderungsantragRoutingFactory,
    ChatRoutingFactory,
    ComponentService,
    AnfrageLanguageService,
    { provide: IdTokenRepository, useClass: LocalStorageIdTokenRepository },
    { provide: XFoundHttpClient, useClass: HttpClient },
    {
      provide: ErrorHandler,
      useClass: GlobalNotificationHandler,
    },
    httpInterceptorProviders,
    CldrIntlService,
    { provide: IntlService, useExisting: CldrIntlService },
    { provide: MessageService, useClass: KendoMessageService },
    { provide: NotificationService, useClass: ExtendedNotificationService },
    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    protected stammdatenService: StammdatenService,
    protected router: Router,
    protected portalContextService: PortalContextService,
    protected sectionService: SectionService,
    protected notificationService: NotificationService,
    protected authenticationService: ExtendedAuthenticationService
  ) {
    this.stammdatenService.GetInfoTexte().subscribe();
    this.stammdatenService.GetPortalKonfiguration().subscribe();
    this.authenticationService.GetUser().subscribe();
    this.sectionService.initRouter(this.router);
    this.router.events.subscribe((event) => this.onRouteNavigationEvent(event));
  }

  onRouteNavigationEvent(event: any) {
    if (event instanceof NavigationStart) {
      this.checkUrlAndRemoveNotifications(event);
      this.portalContextService.setUrl(event.url);
    }
  }

  private checkUrlAndRemoveNotifications(event: any) {
    if (event.url !== this.portalContextService.Url) {
      let splitEventUrl = event?.url?.split('/', 3);
      let splitOldUrl = this.portalContextService?.Url?.split('/', 3);
      
      if (JSON.stringify(splitEventUrl) != JSON.stringify(splitOldUrl))
        (this.notificationService as ExtendedNotificationService).removeAllNotifications();
    }
  }
}
